import * as React from "react"
import { cn } from "../../lib/utils"
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from "../ui/carousel"

export interface LoginHeroSlideProps extends React.HTMLAttributes<HTMLDivElement> {
    label?: string
}

export const LoginHeroSlide = React.forwardRef<HTMLDivElement, LoginHeroSlideProps>(
    ({ label, className, children, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                "h-full w-full bg-gray-100 flex items-center justify-center relative overflow-hidden bg-[#FFFAEF]",
                className
            )}
            {...props}
        >
            {/* <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-gray-200" /> */}
            {<div className="relative z-10 h-full mx-auto mt-4 bg-[#FFFAEF]">{children}</div>}
            {label ? (
                <div className="text-gray-400 text-sm font-medium z-10">{label}</div>
            ) : null}
        </div>
    )
)
LoginHeroSlide.displayName = "LoginHeroSlide"

export interface LoginHeroCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
    slides?: React.ReactNode[]
    initialIndex?: number
    onIndexChange?: (index: number) => void
    placeholderLabel?: string
    autoScrollInterval?: number
}

export const LoginHeroCarousel = React.forwardRef<HTMLDivElement, LoginHeroCarouselProps>(
    (
        {
            slides,
            initialIndex = 0,
            onIndexChange,
            placeholderLabel = "Image Placeholder",
            autoScrollInterval = 3500,
            className,
            ...props
        },
        ref
    ) => {
        const fallbackSlides = React.useMemo(
            () => [<LoginHeroSlide key="login-hero-slide-placeholder" label={placeholderLabel} />],
            [placeholderLabel]
        )
        const safeSlides = slides && slides.length > 0 ? slides : fallbackSlides
        const maxIndex = Math.max(safeSlides.length - 1, 0)
        const [api, setApi] = React.useState<CarouselApi | null>(null)
        const [currentIndex, setCurrentIndex] = React.useState(
            Math.min(Math.max(initialIndex, 0), maxIndex)
        )

        React.useEffect(() => {
            if (!api) {
                return
            }
            const clampedIndex = Math.min(Math.max(initialIndex, 0), maxIndex)
            api.scrollTo(clampedIndex, true)
        }, [api, initialIndex, maxIndex])

        React.useEffect(() => {
            if (!api) {
                return
            }
            const handleSelect = () => {
                const nextIndex = api.selectedScrollSnap()
                setCurrentIndex(nextIndex)
                onIndexChange?.(nextIndex)
            }
            handleSelect()
            api.on("select", handleSelect)
            api.on("reInit", handleSelect)
            return () => {
                api.off("select", handleSelect)
                api.off("reInit", handleSelect)
            }
        }, [api, onIndexChange])

        React.useEffect(() => {
            setCurrentIndex((prev) => Math.min(prev, maxIndex))
        }, [maxIndex])

        React.useEffect(() => {
            if (!api || autoScrollInterval <= 0) {
                return
            }
            const intervalId = window.setInterval(() => {
                api.scrollNext()
            }, autoScrollInterval)
            return () => {
                window.clearInterval(intervalId)
            }
        }, [api, autoScrollInterval])

        return (
            <div ref={ref} className={cn("relative w-full overflow-hidden", className)} {...props}>
                <Carousel
                    setApi={setApi}
                    opts={{ loop: true }}
                    className="h-full w-full"
                >
                    <CarouselContent className="h-full ml-0">
                        {safeSlides.map((slide, index) => (
                            <CarouselItem key={`login-hero-slide-${index}`} className="h-full pl-0">
                                {slide}
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>

                {safeSlides.length > 1 && (
                    <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-2">
                        {safeSlides.map((_, index) => (
                            <button
                                key={`login-hero-dot-${index}`}
                                type="button"
                                aria-label={`Go to slide ${index + 1}`}
                                className={cn(
                                    "h-2.5 w-2.5 rounded-full border border-black transition-colors",
                                    index === currentIndex ? "bg-black" : "bg-white/70"
                                )}
                                onClick={() => api?.scrollTo(index)}
                            />
                        ))}
                    </div>
                )}
            </div>
        )
    }
)

LoginHeroCarousel.displayName = "LoginHeroCarousel"
